/**
 * Contrato da Central de Tributos (Financeiro › Tributos) e do Calendário fiscal (Configurações) — DS-615,
 * D-172, D-173.
 *
 * O tributo próprio tem duas metades separadas: o FATO, que a DRE já reconhece (a dedução pela alíquota
 * sobre a NF; na Fase 1b, a provisão de IRPJ e CSLL), e a OBRIGAÇÃO, que nasce quando a apuração do período
 * fecha: a guia, uma conta a pagar na 8.7 Tributos a recolher. Pagar a guia não toca a DRE.
 *
 * Sem regra no calendário fiscal não há data nem apuração: o período fica em "Aguardando configuração
 * fiscal" (pedido de 01/10, §4).
 */
import { z } from 'zod'
import { dataNominal, uuid } from './comum.js'
import { dinheiroNaoNegativo, dinheiroPositivo } from './contas-pagar.js'

export const tributoFiscal = z.enum(['ISS', 'PIS', 'COFINS', 'CBS', 'IBS', 'IRPJ', 'CSLL'])
export type TributoFiscalDoContrato = z.infer<typeof tributoFiscal>
export const periodicidadeFiscal = z.enum(['mensal', 'trimestral'])
export const regraDoDiaFiscal = z.enum(['dia_fixo', 'ultimo_dia_de_semana'])
export const ajusteDeFimDeSemana = z.enum(['antecipa', 'posterga'])
export const regraDeReconhecimento = z.enum(['mensal', 'no_fechamento'])

const primeiroDiaDoMes = dataNominal.refine((d) => d.endsWith('-01'), 'Use o primeiro dia do mês.')

// ------------------------------------------------------------------ calendário fiscal

export const regraDoCalendarioFiscal = z.object({
  id: uuid,
  tributo: tributoFiscal,
  periodicidade: periodicidadeFiscal,
  mesesAposPeriodo: z.number().int().min(0).max(3),
  regraDia: regraDoDiaFiscal,
  dia: z.number().int().min(1).max(31).nullable(),
  ajusteFimDeSemana: ajusteDeFimDeSemana.nullable(),
  /** IRPJ e CSLL (Fase 1b): todo mês, ou só no fechamento da apuração. */
  regraReconhecimento: regraDeReconhecimento.nullable(),
  /** IRPJ e CSLL: a conta da DRE, escolhida aqui (a D-165 não lhes dá chave). */
  contaContabilId: uuid.nullable(),
  contaContabil: z.string().nullable(),
  centroCustoId: uuid,
  centroCusto: z.string(),
  fonte: z.string(),
  vigenciaDe: dataNominal,
  vigenciaAte: dataNominal.nullable(),
  /** A regra em voz de produto ("Dia 25 do mês seguinte ao período; no fim de semana, antecipa para a sexta"). */
  descricaoDoVencimento: z.string(),
  vigente: z.boolean(),
})
export type RegraDoCalendarioFiscal = z.infer<typeof regraDoCalendarioFiscal>

export const entradaDeRegraFiscal = z.object({
  tributo: tributoFiscal,
  periodicidade: periodicidadeFiscal,
  mesesAposPeriodo: z.number().int().min(0, 'De 0 a 3 meses.').max(3, 'De 0 a 3 meses.'),
  regraDia: regraDoDiaFiscal,
  dia: z.number().int().min(1, 'Dia de 1 a 31.').max(31, 'Dia de 1 a 31.').nullable(),
  ajusteFimDeSemana: ajusteDeFimDeSemana.nullable(),
  regraReconhecimento: regraDeReconhecimento.nullable(),
  contaContabilId: uuid.nullable(),
  centroCustoId: uuid,
  fonte: z.string().trim().min(1, 'Diga de onde vem a regra (legislação, contador).').max(300),
  vigenciaDe: dataNominal,
  vigenciaAte: dataNominal.nullable(),
}).superRefine((e, contexto) => {
  if (e.regraDia === 'dia_fixo' && (e.dia === null || e.ajusteFimDeSemana === null)) {
    contexto.addIssue({ code: 'custom', path: ['dia'], message: 'No dia fixo, informe o dia e o que fazer no fim de semana.' })
  }
  if (e.regraDia === 'ultimo_dia_de_semana' && (e.dia !== null || e.ajusteFimDeSemana !== null)) {
    contexto.addIssue({ code: 'custom', path: ['dia'], message: 'O último dia de semana não tem dia fixo.' })
  }
  const doResultado = e.tributo === 'IRPJ' || e.tributo === 'CSLL'
  if (doResultado && (e.regraReconhecimento === null || e.contaContabilId === null)) {
    contexto.addIssue({ code: 'custom', path: ['contaContabilId'], message: 'IRPJ e CSLL pedem a regra de reconhecimento e a conta da DRE.' })
  }
  if (!doResultado && (e.regraReconhecimento !== null || e.contaContabilId !== null)) {
    contexto.addIssue({ code: 'custom', path: ['contaContabilId'], message: 'Só IRPJ e CSLL têm regra de reconhecimento e conta.' })
  }
  if (e.vigenciaAte !== null && e.vigenciaAte < e.vigenciaDe) {
    contexto.addIssue({ code: 'custom', path: ['vigenciaAte'], message: 'O fim vem depois do início.' })
  }
})
export type EntradaDeRegraFiscal = z.infer<typeof entradaDeRegraFiscal>

/** Mudar a regra é encerrar a vigência desta e cadastrar outra; a fonte pode ser corrigida. */
export const entradaDeEncerramentoDeRegraFiscal = z.object({
  vigenciaAte: dataNominal,
})

export const opcoesDoCalendarioFiscal = z.object({
  contas: z.array(z.object({ id: uuid, rotulo: z.string() })),
  centros: z.array(z.object({ id: uuid, rotulo: z.string() })),
})
export type OpcoesDoCalendarioFiscal = z.infer<typeof opcoesDoCalendarioFiscal>

// ------------------------------------------------------------------ apuração

export const situacaoDaApuracao = z.enum([
  /** Sem regra vigente no calendário fiscal: sem período, sem data, sem apuração. */
  'aguardando_configuracao',
  /** Há regra; ninguém fechou o período. */
  'a_apurar',
  /** Fechada, guia em aberto (ou fechada sem guia, com valor zero). */
  'fechada',
  'parcialmente_paga',
  'paga',
])
export type SituacaoDaApuracao = z.infer<typeof situacaoDaApuracao>

export const linhaDaApuracao = z.object({
  tributo: tributoFiscal,
  /** Sem regra: o mês da competência, e o período não existe. */
  periodoInicio: dataNominal,
  periodoFim: dataNominal.nullable(),
  rotuloDoPeriodo: z.string(),
  /** O que a DRE tem hoje na conta do tributo no período. */
  reconhecido: z.string(),
  apurado: z.string().nullable(),
  ajuste: z.string().nullable(),
  valorGuia: z.string().nullable(),
  vencimento: dataNominal.nullable(),
  pago: z.string().nullable(),
  situacao: situacaoDaApuracao,
  apuracaoId: uuid.nullable(),
  sequencia: z.number().int().nullable(),
  guia: z.object({ contaPagarId: uuid, codigo: z.string() }).nullable(),
  /** O vencimento que o calendário dá ao período, quando há regra (a proposta do fechamento). */
  vencimentoDoCalendario: dataNominal.nullable(),
  /** A guia que o fechamento propõe: o reconhecido menos o que guias anteriores do período já cobrem. */
  guiaProposta: z.string().nullable(),
})
export type LinhaDaApuracao = z.infer<typeof linhaDaApuracao>

/**
 * DS-631: o tributo da nota que já teve regra (o mês vem no da primeira vigência dele ou depois), sem regra vigente num
 * mês com NF autorizada. A DRE não deduz nada dele nesse mês (DS-601) e nada presume 0%: o aviso diz que não houve
 * cálculo.
 */
export const tributoSemRegraVigente = z.object({
  tributo: z.enum(['ISS', 'PIS', 'COFINS', 'CBS', 'IBS']),
  /** O mês (dia 1) da emissão das NFs autorizadas sem regra. */
  competencia: dataNominal,
  notas: z.number().int().positive(),
})
export type TributoSemRegraVigente = z.infer<typeof tributoSemRegraVigente>

export const respostaDasApuracoes = z.object({
  data: z.array(linhaDaApuracao),
  meta: z.object({
    ano: z.number().int(),
    /** Os tributos com reconhecido no ano e sem regra no calendário: o aviso da tela. */
    aguardandoConfiguracao: z.array(tributoFiscal),
    /** DS-616: sem regime fiscal na entidade, IRPJ e CSLL não fecham (o sistema nunca o preenche). */
    regimeFiscalAusente: z.boolean(),
    /** DS-631: os meses com NF autorizada em que um tributo da nota que já teve regra ficou sem regra vigente. */
    semRegraTributaria: z.array(tributoSemRegraVigente),
  }),
})
export type RespostaDasApuracoes = z.infer<typeof respostaDasApuracoes>

export const entradaDeFechamentoDaApuracao = z.object({
  tributo: tributoFiscal,
  periodoInicio: primeiroDiaDoMes,
  apurado: dinheiroNaoNegativo,
  /** Ausente = a proposta (o apurado, ou o que falta pagar na complementar). Outro valor pede motivo. */
  valorGuia: dinheiroNaoNegativo.optional(),
  /** Ausente = o do calendário. Outro dia pede motivo. */
  vencimento: dataNominal.optional(),
  /** Ausente = o último mês do período. Outra competência pede motivo. */
  competenciaAjuste: primeiroDiaDoMes.optional(),
  motivo: z.string().trim().min(1).max(500).optional(),
})
export type EntradaDeFechamentoDaApuracao = z.infer<typeof entradaDeFechamentoDaApuracao>

export const entradaDeReaberturaDaApuracao = z.object({
  motivo: z.string().trim().min(1, 'Diga por que a apuração vai ser refeita.').max(500),
})

// ------------------------------------------------------------------ provisão de IRPJ/CSLL (DS-616)

export const origemDaProvisao = z.enum(['contador', 'estimativa_interna'])

export const entradaDeProvisao = z.object({
  tributo: z.enum(['IRPJ', 'CSLL']),
  competencia: primeiroDiaDoMes,
  valor: dinheiroPositivo,
  origem: origemDaProvisao,
  observacao: z.string().trim().min(1).max(500).optional(),
})
export type EntradaDeProvisao = z.infer<typeof entradaDeProvisao>

export const entradaDeCancelamentoDeProvisao = z.object({
  motivo: z.string().trim().min(1, 'Diga por que a provisão sai.').max(500),
})

export const provisaoDoDetalhe = z.object({
  id: uuid,
  competencia: dataNominal,
  valor: z.string(),
  origem: origemDaProvisao,
  observacao: z.string().nullable(),
  estado: z.enum(['ativa', 'cancelada']),
  criadaPor: z.string(),
  criadaEm: z.string(),
  motivoCancelamento: z.string().nullable(),
})
export type ProvisaoDoDetalhe = z.infer<typeof provisaoDoDetalhe>

export const apuracaoDoDetalhe = z.object({
  id: uuid,
  sequencia: z.number().int(),
  estado: z.enum(['fechada', 'reaberta']),
  reconhecido: z.string(),
  apurado: z.string(),
  ajuste: z.string(),
  competenciaAjuste: dataNominal,
  valorGuia: z.string(),
  vencimento: dataNominal.nullable(),
  vencimentoManual: z.boolean(),
  motivo: z.string().nullable(),
  fechadaEm: z.string(),
  fechadaPor: z.string(),
  reabertaEm: z.string().nullable(),
  reabertaPor: z.string().nullable(),
  motivoReabertura: z.string().nullable(),
  guia: z.object({
    contaPagarId: uuid,
    codigo: z.string(),
    status: z.string(),
    pagamentos: z.array(z.object({ data: dataNominal, valor: z.string() })),
  }).nullable(),
})
export type ApuracaoDoDetalhe = z.infer<typeof apuracaoDoDetalhe>

export const detalheDaApuracao = z.object({
  linha: linhaDaApuracao,
  /** O reconhecido mês a mês dentro do período (a soma é o reconhecido da linha). */
  reconhecidoPorCompetencia: z.array(z.object({ competencia: dataNominal, valor: z.string() })),
  /** IRPJ e CSLL: as provisões informadas no período, ativas e canceladas. */
  provisoes: z.array(provisaoDoDetalhe),
  apuracoes: z.array(apuracaoDoDetalhe),
  trilha: z.array(z.object({
    quando: z.string(), ator: z.string().nullable(), acao: z.string(), resumo: z.string(),
  })),
})
export type DetalheDaApuracao = z.infer<typeof detalheDaApuracao>
